if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // OPTIMISTIC DRAWER (Yen Aura, 24 Sep 2026 speed pass).
        // The cart/add round trip takes ~1.5s on this store because Rapi, Bogos
        // and Appstle all hook it server side, and nothing the theme does can
        // make that faster. So open the drawer NOW, dimmed with a spinner, and
        // let renderContents() fill it when the response lands. Perceived time
        // to a visible reaction drops from ~2000ms to ~100ms.
        // Deliberately additive and wrapped: if any of this throws, the original
        // flow is untouched and renderContents() still opens the drawer as before.
        // 18 Oct 2026 (v5.9.1): window.yenCartWatch (assets/yen-perf.js) adds the
        // slow-connection messages and real-cart checks while this is pending.
        this.yenNetFail = false;
        // 19 Oct 2026 (v5.10 audit): remember that THIS add opened the drawer. The error paths below used to
        // check the drawer's yen-pending class, which is cleared early when the cart already holds items, so
        // a refused or failed add left the drawer open on the old cart with the error hidden behind it.
        this.yenOpened = false;
        try {
          if (
            this.cart &&
            typeof this.cart.open === 'function' &&
            !this.cart.classList.contains('active')
          ) {
            this.yenOpened = true;
            this.cart.classList.add('yen-pending');
            if (typeof window.yenCartWatch === 'function') window.yenCartWatch(this.cart);
            this.cart.open(this.submitButton);
          }
        } catch (e) {
          /* non-fatal: fall through to the normal flow */
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              // Undo the optimistic open so the error is not hidden behind the drawer.
              try {
                if (this.cart && (this.yenOpened || this.cart.classList.contains('yen-pending'))) {
                  this.cart.classList.remove('yen-pending', 'yen-slow', 'yen-stuck');
                  if (typeof this.cart.close === 'function') this.cart.close();
                }
              } catch (e) {
                /* non-fatal */
              }

              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);

            // 18 Oct 2026 (v5.9.1): the request itself failed (dropped connection).
            // Before, the drawer was left open and blank. Now yen-perf.js checks the
            // real cart: if the item got in, the drawer shows it; if not, the drawer
            // closes and the shopper sees why under the button.
            const failText = "Couldn't add to cart. Please check your connection and try again.";
            if (this.cart && (this.yenOpened || this.cart.classList.contains('yen-pending')) && typeof window.yenCartFailed === 'function') {
              this.yenNetFail = true;
              window.yenCartFailed(this.cart, () => this.handleErrorMessage(failText));
            } else {
              this.handleErrorMessage(failText);
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            // After a network failure yen-perf.js owns the drawer state (it shows
            // the real cart or closes the drawer), so leave it alone here.
            if (!this.yenNetFail) {
              try {
                if (this.cart) this.cart.classList.remove('yen-pending');
              } catch (e) {
                /* non-fatal */
              }
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          // 18 Oct 2026 (v5.9.1): on the PDP the message sits above the pack picker,
          // out of sight of the Add button the shopper just tapped. Bring it into view.
          try {
            const r = this.errorMessageWrapper.getBoundingClientRect();
            if (r.top < 80 || r.bottom > window.innerHeight) {
              this.errorMessageWrapper.scrollIntoView({ block: 'center' }); // instant: a smooth scroll was cancelled on the PDP in testing
            }
          } catch (e) {
            /* non-fatal */
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
